import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SntCheckBox from "@cleanup/components/SntCheckBox/SntCheckBox";
import useOuterClick from "@cleanup/hooks/useOutsideElement";
import ExcludeLabel from "@cleanup/components/AdvancedFilters/ExcludeLabel";
import GeneralFooterFilter from "@cleanup/components/GeneralFooterFilter/GeneralFooterFilter";
import searchUIClient from "@/apis/searchUIClient";
import { Button, Container, Form } from "react-bootstrap";
import {
  AdvanceFilter,
  FilterBody,
  FilterHeaderStyle,
  FilterOptionHint,
} from "@cleanup/components/AdvancedFilters/FilterStyle";
import SntCloseSmallIcon from "@icons/SntCloseSmallIcon";
import SntArrowDownIcon from "@icons/SntArrowDownIcon";
import usePositionFilter from "@cleanup/hooks/usePositionFilter";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

interface FilterTypeSettings {
  optionsURL?: string;
  notDefinedOption?: {
    headerLabel?: string;
    optionLabel?: string;
    optionHint?: number;
  };
  definedOption?: {
    headerLabel?: string;
    optionLabel?: string;
    optionHint?: number;
  };
  numInputFieldLines?: number;
  searchFieldPlaceholder?: string;
  showOptionHints?: boolean;
}

interface Descriptor {
  _idx: string;
  description: string;
  label?: string;
  filterTypeSettings: FilterTypeSettings;
}

interface FilterValue {
  searchText?: string;
  includeEmpty?: boolean;
  includeNoEmpty?: boolean;
}

interface FilterData {
  filterValue?: FilterValue;
  notFilter?: boolean;
  idx?: string;
}

interface StringFilterProps {
  descriptor: Descriptor;
  data?: FilterData;
  onChange?: (data: FilterData | { idx: string; data: null }) => void;
  disabled?: boolean;
}

interface DataState {
  searchText: string;
  includeEmpty: boolean;
  includeNoEmpty: boolean;
  notFilter: boolean;
}

interface CountData {
  notDefinedCount: number;
  definedCount: number;
}

const StringFilter: React.FC<StringFilterProps> = ({
  descriptor,
  data = {},
  onChange,
  disabled = false,
}) => {
  const [isShow, setShow] = useState(false);
  const [disabledText, setDisabledText] = useState(false);
  const [title, setTitle] = useState("");
  const [countData, setCountData] = useState<CountData>({
    notDefinedCount: 0,
    definedCount: 0,
  });

  const [dataState, setDataState] = useState<DataState>({
    searchText: "",
    includeEmpty: false,
    includeNoEmpty: false,
    notFilter: false,
  });

  const popupStyleRef = useRef<Record<string, unknown>>({});
  const { popupRef, buttonRef, getPosition } = usePositionFilter();

  useEffect(
    () => {
      if (isShow === false && descriptor.filterTypeSettings.optionsURL) {
        searchUIClient
          .getSuggestion({ url: descriptor.filterTypeSettings.optionsURL })
          .then(({ data }) => {
            setCountData({
              notDefinedCount:
                (data.notDefinedOption && data.notDefinedOption.optionHint) ||
                0,
              definedCount:
                (data.definedOption && data.definedOption.optionHint) || 0,
            });
          })
          .catch(() => {
            // Ignore errors
          });
      }
    },
    [isShow, descriptor.filterTypeSettings.optionsURL]
  );

  useEffect(() => {
    const { filterValue } = data;
    let _title = translate(LanguageKey.all_key);
    const _dataState: DataState = {
      searchText: "",
      includeEmpty: false,
      includeNoEmpty: false,
      notFilter: false,
    };

    if (filterValue) {
      Object.assign(_dataState, filterValue);

      if (filterValue.searchText) {
        _title = '"' + filterValue.searchText + '"';
      }

      if (filterValue.includeEmpty) {
        _title =
          descriptor.filterTypeSettings?.notDefinedOption?.headerLabel || "";
      }

      if (filterValue.includeNoEmpty) {
        _title =
          descriptor.filterTypeSettings?.definedOption?.headerLabel || "";
      }
    }
    _dataState.notFilter = Boolean(data.notFilter);

    setDataState(_dataState);
    setTitle(_title);
  }, [data, data.filterValue, descriptor]);

  const getData = useCallback(() => {
    const newData = JSON.parse(JSON.stringify(data)) as FilterData;
    newData.notFilter = dataState.notFilter;

    const hasEmptySearchText =
      !dataState.searchText || dataState.searchText.trim() === "";
    const hasNoOtherFilters =
      !dataState.includeEmpty && !dataState.includeNoEmpty;

    if (hasEmptySearchText && hasNoOtherFilters) {
      delete newData.filterValue;
    } else {
      newData.filterValue = {
        includeEmpty: dataState.includeEmpty,
        includeNoEmpty: dataState.includeNoEmpty,
        searchText:
          dataState.includeEmpty || dataState.includeNoEmpty
            ? undefined
            : dataState.searchText,
      };
    }

    return newData;
  }, [data, dataState]);

  const isChanged = useCallback(() => {
    const oldDataFilter = data.filterValue || {};
    const newData = getData();
    const newDataFilter = newData.filterValue || {};

    const oldSearchText = oldDataFilter.searchText || "";
    const newSearchText = newDataFilter.searchText || "";

    if (
      (oldDataFilter.includeEmpty === newDataFilter.includeEmpty ||
        (oldDataFilter.includeEmpty === undefined &&
          newDataFilter.includeEmpty === false) ||
        (oldDataFilter.includeEmpty === false &&
          newDataFilter.includeEmpty === undefined)) &&
      (oldDataFilter.includeNoEmpty === newDataFilter.includeNoEmpty ||
        (oldDataFilter.includeNoEmpty === undefined &&
          newDataFilter.includeNoEmpty === false) ||
        (oldDataFilter.includeNoEmpty === false &&
          newDataFilter.includeNoEmpty === undefined)) &&
      oldSearchText.trim() === newSearchText.trim() &&
      (data.notFilter === newData.notFilter ||
        (data.notFilter === undefined && newData.notFilter === false) ||
        (data.notFilter === false && newData.notFilter === undefined))
    ) {
      return false;
    }
    return true;
  }, [data, getData]);

  const refWrapper = useOuterClick(() => {
    if (isShow) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData());
        }
      }
    }
  });

  const onClickButton = () => {
    if (isShow) {
      setShow(false);
      if (isChanged()) {
        if (onChange) {
          onChange(getData());
        }
      }
    } else {
      popupStyleRef.current = getPosition();
      setShow(true);
    }
  };

  const onAppliedFilter = () => {
    setShow(false);
    if (isChanged()) {
      if (onChange) {
        onChange(getData());
      }
    }
  };

  const onClearFilter = () => {
    setShow(false);
    setDataState({
      searchText: "",
      includeEmpty: false,
      includeNoEmpty: false,
      notFilter: false,
    });
    if (onChange) {
      onChange({
        idx: descriptor._idx,
        data: null,
      });
    }
  };

  const excludeLabel = useMemo(() => {
    return data.notFilter && <ExcludeLabel />;
  }, [data.notFilter]);

  useEffect(() => {
    if (dataState.includeEmpty || dataState.includeNoEmpty) {
      setDisabledText(true);
    } else {
      setDisabledText(false);
    }
  }, [dataState.includeEmpty, dataState.includeNoEmpty]);

  const filterView = useMemo(() => {
    const { filterTypeSettings } = descriptor;
    if (filterTypeSettings) {
      const { notDefinedOption, definedOption } = filterTypeSettings;
      return (
        <>
          {notDefinedOption && (
            <div className="d-flex justify-content-between mb-3">
              <SntCheckBox
                checked={dataState.includeEmpty}
                onChange={(value) =>
                  setDataState({
                    ...dataState,
                    includeEmpty: !dataState.includeEmpty && value,
                    includeNoEmpty: dataState.includeNoEmpty && !value,
                  })
                }
                label={<span>{notDefinedOption.optionLabel}</span>}
              />
              {filterTypeSettings.showOptionHints && (
                <FilterOptionHint>
                  {countData.notDefinedCount || 0}
                </FilterOptionHint>
              )}
            </div>
          )}
          {definedOption && (
            <div className="mb-3">
              <SntCheckBox
                checked={dataState.includeNoEmpty}
                onChange={(value) =>
                  setDataState({
                    ...dataState,
                    includeNoEmpty: !dataState.includeNoEmpty && value,
                    includeEmpty: dataState.includeEmpty && !value,
                  })
                }
                label={definedOption.optionLabel}
              />
              {filterTypeSettings.showOptionHints && (
                <FilterOptionHint>
                  {countData.definedCount || 0}
                </FilterOptionHint>
              )}
            </div>
          )}
        </>
      );
    }
    return null;
  }, [descriptor, countData, dataState]);

  return (
    <AdvanceFilter ref={refWrapper}>
      <Button
        ref={buttonRef}
        variant="sensolus-greylight"
        title={descriptor.description}
        disabled={disabled}
        onClick={onClickButton}
      >
        {(dataState.searchText !== "" ||
          dataState.includeEmpty ||
          dataState.includeNoEmpty) && (
          <SntCloseSmallIcon
            className="me-1"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClearFilter();
            }}
          />
        )}
        <span>{descriptor.label || descriptor.description}</span>:{" "}
        <span>{excludeLabel}</span>
        <FilterHeaderStyle>{title}</FilterHeaderStyle>
        <SntArrowDownIcon />
      </Button>
      <FilterBody
        ref={popupRef}
        style={{ display: isShow ? "block" : "none", ...popupStyleRef.current }}
      >
        <Container>
          <Form.Control
            as="textarea"
            name="searchText"
            rows={descriptor.filterTypeSettings.numInputFieldLines}
            placeholder={descriptor.filterTypeSettings.searchFieldPlaceholder}
            style={{ resize: "none" }}
            disabled={disabledText}
            value={dataState.searchText}
            onChange={(e) => {
              setDataState({ ...dataState, searchText: e.target.value });
            }}
            className="mb-3 mt-3"
          />
          {filterView}
        </Container>
        <GeneralFooterFilter
          onClearFilter={() => onClearFilter()}
          onAppliedFilter={() => onAppliedFilter()}
          isNotFilter={dataState.notFilter}
          onCheckNotFilter={(e) => {
            setDataState({
              ...dataState,
              notFilter: e,
            });
          }}
          disableCheckBox={
            dataState.searchText === "" ||
            dataState.includeEmpty ||
            dataState.includeNoEmpty
          }
        />
      </FilterBody>
    </AdvanceFilter>
  );
};

export default StringFilter;
